<template>
  <div class="custom-calendar">
    <AIVHorizontalScroll :sticky-offset="64">
    <div class="calendar-week-view">
      <div class="week-time-header">
        <div class="date-label"></div>
        <div
          class="time-slot-header"
          v-for="(time, idx) in timeSlots"
          :key="idx"
        >
          {{ time }}
        </div>
      </div>

      <div
        class="calendar-row"
        v-for="(date, idx) in weekDates"
        :key="idx"
        :style="{ height: `${calculateRowHeight(date)}px` }"
      >
        <div class="date-label">
          {{ dayNames[date.getDay()] }} {{ date.getDate() }}
        </div>

        <div class="time-slots-container">
          <div
            v-if="isTodayInWeek"
            class="current-time-line"
            :style="lineStyle"
          ></div>
          <!-- Time Off Indicator -->
          <div v-if="isDateOnTimeOff(date)" class="time-off-bar" :style="computeTimeOffStyle(isDateOnTimeOff(date))">
            <i class="fas fa-umbrella-beach"></i>
            <span>{{ formatTimeOffDisplay(isDateOnTimeOff(date)) }}</span>
          </div>
          <el-tooltip
            v-for="shift in eventsForDate(date)"
            :key="shift.id"
            placement="bottom"
            effect="light"
            :open-delay="300"
            :disabled="shift.isAvailability"
          >
            <div slot="content" v-html="formatShiftTooltipHtml(shift)"></div>
            <div
              class="shift-block"
              :class="[
                shift.statusClass,
                getPayerClass(shift),
                getShiftTypeClass(shift),
                { 'availability-slot': shift.isAvailability }
              ]"
              :style="computeEventStyle(shift)"
              @click.stop="shift.isAvailability ? null : $emit('show-details', shift)"
            >
              <template v-if="shift.isAvailability">
                <i class="fas fa-clock"></i>
                <span class="availability-time">{{ shift.formattedStartTime }} - {{ shift.formattedEndTime }}</span>
              </template>
              <template v-else>
                <span class="shift-time">
                  {{ shift.formattedStartTime }}
                </span>
                <span class="shift-info">{{ shift.groupName || shift.client }}</span>
                <span class="shift-caregiver" v-if="!shift.groupName && shift.caregiver && shift.caregiver !== 'Unassigned'">{{ shift.caregiver }}</span>
              </template>
            </div>
          </el-tooltip>
        </div>
      </div>
    </div>
    </AIVHorizontalScroll>
  </div>
</template>

<script>
import AIVHorizontalScroll from "@/components/AIVHorizontalScroll.vue";
import { isDSPDGroupShift, getCaregiverShiftStatus, getStatusIcon, formatCaregiverStatusHtml, caregiverNameWithRole } from '@/utils/shiftStatus';

export default {
  name: "WeekView",
  components: { AIVHorizontalScroll },
  props: {
    weekDates: { type: Array, required: true },
    dayNames: { type: Array, required: true },
    timeSlots: { type: Array, required: true },
    eventsForDate: { type: Function, required: true },
    isTodayInWeek: { type: Boolean, required: true },
    lineStyle: { type: Object, required: true },
    computeEventStyle: { type: Function, required: true },
    userRole: { type: String, default: "Admin" },
    approvedTimeOff: { type: Array, default: () => [] },
  },
  methods: {
    calculateRowHeight(date) {
      const shifts = this.eventsForDate(date);

      // Check if there's time off for this date
      const timeOff = this.isDateOnTimeOff(date);
      const hasFullDayTimeOff = timeOff && timeOff.request_type !== 'single_partial_day';

      // Check if there's availability for this date
      const hasAvailability = shifts.some((s) => s.isAvailability);

      // If no shifts but there is time off, show minimum height with time off bar
      if (shifts.length === 0 && timeOff) return 60;

      if (shifts.length === 0) return 60;

      // Calculate lanes to determine how many shifts overlap
      // Separate availability from regular shifts
      const regularShifts = shifts.filter((s) => !s.isAvailability);

      const dateShifts = regularShifts.map((s) => {
        const [sh, sm] = s.startTime.split(":").map(Number);
        const [eh, em] = s.endTime.split(":").map(Number);
        return { ...s, _start: sh * 60 + sm, _end: eh * 60 + em };
      });

      const lanes = [];
      dateShifts.forEach((s) => {
        let i = lanes.findIndex((lane) =>
          lane.every((x) => x._end <= s._start)
        );
        if (i === -1) {
          lanes.push([]);
          i = lanes.length - 1;
        }
        lanes[i].push(s);
      });

      // Height calculation: base height + (number of lanes * 28px per lane)
      // 28px accounts for 24px shift height + 4px spacing
      // Add lanes for: full-day time off bar (if present) + availability/partial time off (if present) + regular shift lanes
      // Note: partial-day time off shares the same lane as availability since they don't overlap
      let extraLanes = 0;
      if (hasFullDayTimeOff) extraLanes += 1;
      if (hasAvailability || (timeOff && timeOff.request_type === 'single_partial_day')) extraLanes += 1;

      const numLanes = lanes.length + extraLanes;
      return Math.max(60, 30 + numLanes * 28);
    },
    getPayerClass(shift) {
      if (this.userRole === "Caregiver") {
        return "";
      }
      const payer = shift.payer || 'DSPD';
      return `payer-${payer.toLowerCase().replace(/\s+/g, '-')}`;
    },
    getShiftTypeClass(shift) {
      const shiftType = shift.shiftType || '1-on-1';
      return `shift-type-${shiftType.toLowerCase().replace(/\s+/g, '-')}`;
    },
    formatShiftTooltip(shift) {
      let tooltip = ``;

      // Show primary display times first (actual or scheduled)
      if (shift.hasClockIn || shift.hasClockOut) {
        const displayStart = shift.formattedStartTime;
        const displayEnd = shift.formattedEndTime;
        
        if (shift.hasClockOut) {
          tooltip += `Actual: ${displayStart} - ${displayEnd}`;
          // Calculate hours worked from actual times
          const actualStart = shift.actualStartTime || shift.formattedStartTime;
          const actualEnd = shift.actualEndTime || shift.formattedEndTime;
          if (actualStart && actualEnd) {
            const startParts = actualStart.split(':').map(Number);
            const endParts = actualEnd.split(':').map(Number);
            let startMin = startParts[0] * 60 + startParts[1];
            let endMin = endParts[0] * 60 + endParts[1];
            if (endMin < startMin) endMin += 24 * 60; // overnight
            const totalMin = endMin - startMin;
            const hrs = Math.floor(totalMin / 60);
            const mins = totalMin % 60;
            tooltip += `\nHours Worked: ${hrs}:${String(mins).padStart(2, '0')}`;
          }
        } else {
          tooltip += `Actual: ${displayStart} - In Progress`;
        }
        
        // Show scheduled times as reference
        const scheduledStart = shift.formattedScheduledStartTime;
        const scheduledEnd = shift.formattedScheduledEndTime;
        tooltip += `\nScheduled: ${scheduledStart} - ${scheduledEnd}`;
        {
          const sParts = shift.scheduledStartTime.split(':').map(Number);
          const eParts = shift.scheduledEndTime.split(':').map(Number);
          let sMin = sParts[0] * 60 + sParts[1];
          let eMin = eParts[0] * 60 + eParts[1];
          if (eMin < sMin) eMin += 24 * 60;
          const total = eMin - sMin;
          tooltip += `\nHours Scheduled: ${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
        }
      } else {
        // Show only scheduled times when not clocked in
        const scheduledStart = shift.formattedStartTime;
        const scheduledEnd = shift.formattedEndTime;
        tooltip += `Scheduled: ${scheduledStart} - ${scheduledEnd}`;
        {
          const sParts = shift.startTime.split(':').map(Number);
          const eParts = shift.endTime.split(':').map(Number);
          let sMin = sParts[0] * 60 + sParts[1];
          let eMin = eParts[0] * 60 + eParts[1];
          if (eMin < sMin) eMin += 24 * 60;
          const total = eMin - sMin;
          tooltip += `\nHours Scheduled: ${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
        }
      }

      // Display group name once if it exists, then list individual names
      const clientDisplay = shift.clients && shift.clients.length > 1
        ? shift.clients.map(c => c.name).join(', ')
        : shift.client;
      let caregiverDisplay;
      if (isDSPDGroupShift(shift.originalData) && (this.userRole === 'Admin' || this.userRole === 'Lead')) {
        caregiverDisplay = shift.caregivers.map(caregiver => {
          const status = getCaregiverShiftStatus(shift.originalData, caregiver.id);
          const statusIcon = getStatusIcon(status);
          return `${statusIcon} ${caregiver.name}`;
        }).join(', ');
      } else {
        caregiverDisplay = shift.caregivers && shift.caregivers.length > 1
          ? shift.caregivers.map(c => caregiverNameWithRole(c)).join(', ')
          : shift.caregiver;
      }

      const clientLabel = shift.clients && shift.clients.length > 1 ? 'Clients' : 'Client';
      const caregiverLabel = shift.caregivers && shift.caregivers.length > 1 ? 'Caregivers' : 'Caregiver';

      if (shift.groupName) {
        tooltip += `\nGroup: ${shift.groupName}`;
      }
      tooltip += `\n${clientLabel}: ${clientDisplay}`;
      tooltip += `\n${caregiverLabel}: ${caregiverDisplay}`;
      tooltip += `\nPayer: ${shift.payer}`;

      // Get service codes - check both service_codes and non_service_codes
      const serviceCodes = shift.originalData?.service_codes || [];
      const nonServiceCodes = shift.originalData?.non_service_codes || [];

      // Use non_service_codes if payer is Non Service, otherwise use service_codes
      const codesToDisplay = (shift.payer === 'Non Service' ? nonServiceCodes : serviceCodes);
      const serviceCodeList = codesToDisplay.map(sc => sc.code).filter(Boolean).join(', ');

      if (serviceCodeList) {
        tooltip += `\nCode: ${serviceCodeList}`;
      }

      tooltip += `\nShift Type: ${shift.shiftType}`;

      // Capitalize status
      const capitalizedStatus = shift.status ? shift.status.charAt(0).toUpperCase() + shift.status.slice(1) : '';
      tooltip += `\nStatus: ${capitalizedStatus}`;

      return tooltip;
    },
    isGroupShiftForTooltip(shift) {
      return !shift.isAvailability && isDSPDGroupShift(shift.originalData) && (this.userRole === 'Admin' || this.userRole === 'Lead');
    },
    formatShiftTooltipHtml(shift) {
      let lines = [];
      const isGroup = isDSPDGroupShift(shift.originalData) && (this.userRole === 'Admin' || this.userRole === 'Lead');

      const calcHours = (start, end) => {
        const sParts = start.split(':').map(Number);
        const eParts = end.split(':').map(Number);
        let sMin = sParts[0] * 60 + sParts[1];
        let eMin = eParts[0] * 60 + eParts[1];
        if (eMin < sMin) eMin += 24 * 60;
        const total = eMin - sMin;
        return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
      };

      if (!isGroup && (shift.hasClockIn || shift.hasClockOut)) {
        const displayStart = shift.formattedStartTime;
        const displayEnd = shift.formattedEndTime;
        if (shift.hasClockOut) {
          lines.push(`Actual: ${displayStart} - ${displayEnd}`);
          const actualStart = shift.actualStartTime || shift.formattedStartTime;
          const actualEnd = shift.actualEndTime || shift.formattedEndTime;
          if (actualStart && actualEnd) {
            lines.push(`Hours Worked: ${calcHours(actualStart, actualEnd)}`);
          }
        } else {
          lines.push(`Actual: ${displayStart} - In Progress`);
        }
        lines.push(`Scheduled: ${shift.formattedScheduledStartTime} - ${shift.formattedScheduledEndTime}`);
        lines.push(`Hours Scheduled: ${calcHours(shift.scheduledStartTime, shift.scheduledEndTime)}`);
      } else {
        const scheduledStart = isGroup ? shift.formattedScheduledStartTime || shift.formattedStartTime : shift.formattedStartTime;
        const scheduledEnd = isGroup ? shift.formattedScheduledEndTime || shift.formattedEndTime : shift.formattedEndTime;
        const startCalc = isGroup ? shift.scheduledStartTime || shift.startTime : shift.startTime;
        const endCalc = isGroup ? shift.scheduledEndTime || shift.endTime : shift.endTime;
        lines.push(`Scheduled: ${scheduledStart} - ${scheduledEnd}`);
        lines.push(`Hours Scheduled: ${calcHours(startCalc, endCalc)}`);
      }

      const clientDisplay = shift.clients && shift.clients.length > 1
        ? shift.clients.map(c => c.name).join(', ') : shift.client;
      const clientLabel = shift.clients && shift.clients.length > 1 ? 'Clients' : 'Client';

      if (shift.groupName) lines.push(`Group: ${shift.groupName}`);
      lines.push(`${clientLabel}: ${clientDisplay}`);

      const caregiverLabel = shift.caregivers && shift.caregivers.length > 1 ? 'Caregivers' : 'Caregiver';
      if (isDSPDGroupShift(shift.originalData) && (this.userRole === 'Admin' || this.userRole === 'Lead')) {
        const caregiverHtml = formatCaregiverStatusHtml(shift.originalData, shift.caregivers);
        lines.push(`${caregiverLabel}:<br>${caregiverHtml}`);
      } else {
        const caregiverDisplay = shift.caregivers && shift.caregivers.length > 1
          ? shift.caregivers.map(c => caregiverNameWithRole(c)).join(', ') : shift.caregiver;
        lines.push(`${caregiverLabel}: ${caregiverDisplay}`);
      }

      lines.push(`Payer: ${shift.payer}`);
      const serviceCodes = shift.originalData?.service_codes || [];
      const nonServiceCodes = shift.originalData?.non_service_codes || [];
      const codesToDisplay = (shift.payer === 'Non Service' ? nonServiceCodes : serviceCodes);
      const serviceCodeList = codesToDisplay.map(sc => sc.code).filter(Boolean).join(', ');
      if (serviceCodeList) lines.push(`Code: ${serviceCodeList}`);
      lines.push(`Shift Type: ${shift.shiftType}`);
      const capitalizedStatus = shift.status ? shift.status.charAt(0).toUpperCase() + shift.status.slice(1) : '';
      lines.push(`Status: ${capitalizedStatus}`);
      if (shift.miles != null) lines.push(`Miles: ${shift.miles.toFixed(1)}`);

      const cancelReason = shift.originalData?.cancel_reason;
      if (cancelReason) {
        lines.push(`<span style="color:rgb(180, 140, 100);">Canceled: ${cancelReason}</span>`);
      }

      const notes = shift.notes || shift.originalData?.shift_notes;
      if (notes) {
        lines.push(`Notes: ${notes.replace(/\n/g, '<br>')}`);
      }

      return lines.join('<br>');
    },
    isDateOnTimeOff(date) {
      if (!this.approvedTimeOff || this.approvedTimeOff.length === 0) {
        return null;
      }

      const dateStr = date.toISOString().split('T')[0]; // YYYY-MM-DD format

      for (const timeOff of this.approvedTimeOff) {
        if (dateStr >= timeOff.start_date && dateStr <= timeOff.end_date) {
          return timeOff;
        }
      }

      return null;
    },
    isFullDayOff(date) {
      const timeOff = this.isDateOnTimeOff(date);
      if (!timeOff) return false;

      // It's a full day off if it's NOT a partial day
      return timeOff.request_type !== 'single_partial_day';
    },
    formatTimeOffDisplay(timeOff) {
      if (!timeOff) return '';

      // For partial day time off, show the time range
      if (timeOff.request_type === 'single_partial_day' && timeOff.start_time && timeOff.end_time) {
        const startTime = this.formatTime(timeOff.start_time);
        const endTime = this.formatTime(timeOff.end_time);
        return `${startTime} - ${endTime}`;
      }

      // For full day time off
      return 'Full Day Off';
    },
    formatTime(timeStr) {
      if (!timeStr) return '';
      const parts = timeStr.split(':');
      let hours = parseInt(parts[0]);
      const minutes = parts[1] || '00';
      const ampm = hours >= 12 ? 'pm' : 'am';
      hours = hours % 12 || 12;
      return `${hours}:${minutes}${ampm}`;
    },
    computeTimeOffStyle(timeOff) {
      if (!timeOff) return {};

      // For full day time off, span the entire width
      if (timeOff.request_type !== 'single_partial_day') {
        return {
          left: '0',
          right: '0',
          width: 'auto'
        };
      }

      // For partial day time off, calculate position based on time
      // The calendar displays 17 time slots from 6AM to 10PM
      const totalSlots = 17;

      // Parse start and end times
      const [startHour, startMin] = timeOff.start_time.split(':').map(Number);
      const [endHour, endMin] = timeOff.end_time.split(':').map(Number);

      // Calculate slot indices (6AM = 0)
      const startSlotIndex = startHour - 6;
      const endSlotIndex = endHour - 6;

      // Calculate positions with fractional minutes
      const startPosition = startSlotIndex + startMin / 60;
      const endPosition = endSlotIndex + endMin / 60;

      // Convert to percentages
      const startPercent = Math.max((startPosition / totalSlots) * 100, 0);
      const endPercent = Math.min((endPosition / totalSlots) * 100, 100);
      const widthPercent = Math.max(endPercent - startPercent, 1);

      return {
        left: `${startPercent}%`,
        width: `${widthPercent}%`,
        right: 'auto'
      };
    },
  },
};
</script>

<style scoped>
.calendar-week-view {
  position: relative;
  display: flex;
  flex-direction: column;
}
.calendar-week-view .week-time-header {
  display: flex;
  font-weight: bold;
  background: transparent;
}
.calendar-week-view .calendar-row {
  display: flex;
  border-top: 1px solid #ccc;
}
.calendar-week-view .date-label {
  width: 80px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  background: transparent;
}
.calendar-week-view .time-slot-header,
.calendar-week-view .time-slots-container {
  position: relative;
  display: flex;
  width: 100%;
  overflow: hidden;
}
.calendar-week-view .current-time-line {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  background-color: red;
  z-index: 5;
  pointer-events: none;
}
.shift-block {
  position: absolute;
  height: 24px;
  color: #fff;
  font-size: 0.75rem;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 8px;
  white-space: nowrap;
  overflow: hidden;
  z-index: 2;
  cursor: pointer;
  max-width: 100%;
  box-sizing: border-box;
}

.shift-time {
  font-weight: bold;
}

.shift-info {
  overflow: hidden;
  text-overflow: ellipsis;
  opacity: 0.9;
}

.shift-caregiver {
  font-size: 0.6rem;
  color: #e0e0e0;
  font-style: italic;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  opacity: 0.8;
}

/*noinspection CssUnusedSymbol */
.status-unassigned {
  background: linear-gradient(135deg, rgba(32, 201, 151, 0.7), rgba(32, 201, 151, 0.5));  /* Teal for unassigned shifts */
}

/*noinspection CssUnusedSymbol */
.status-scheduled {
  background: linear-gradient(135deg, rgba(52, 144, 220, 0.7), rgba(52, 144, 220, 0.5));  /* Muted blue */
}

/*noinspection CssUnusedSymbol */
.status-clocked-in {
  background: linear-gradient(135deg, rgba(255, 193, 7, 0.9), rgba(255, 193, 7, 0.7));  /* Bright yellow for clocked in */
}

/*noinspection CssUnusedSymbol */
.status-clocked-out {
  background: linear-gradient(135deg, rgba(237, 137, 54, 0.7), rgba(237, 137, 54, 0.5));  /* Muted orange */
}

/*noinspection CssUnusedSymbol */
.status-approved {
  background: linear-gradient(135deg, rgba(156, 39, 176, 0.7), rgba(156, 39, 176, 0.5));  /* Muted deep purple */
}

/*noinspection CssUnusedSymbol */
.status-available {
  background: rgba(64, 158, 255, 0.1);
  border: 1px solid rgba(64, 158, 255, 0.3);
  color: #6ba6cd;
}

.availability-slot {
  cursor: default !important;
  pointer-events: none;
}

.availability-slot i {
  font-size: 0.65rem;
  opacity: 0.8;
  color: #6ba6cd;
}

.availability-time {
  font-size: 0.7rem;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #6ba6cd;
}

/*noinspection CssUnusedSymbol */
.status-missed {
  background: linear-gradient(135deg, rgba(108, 117, 125, 0.8), rgba(108, 117, 125, 0.6));  /* Grey for missed shifts */
}

/*noinspection CssUnusedSymbol */
.status-error {
  background: linear-gradient(135deg, rgba(220, 53, 69, 0.8), rgba(220, 53, 69, 0.6));  /* Red for error shifts */
}

/* Time Off Bar */
.time-off-bar {
  position: absolute;
  top: 0;
  height: 24px;
  background: rgba(255, 152, 0, 0.2);
  border: 1px solid rgba(255, 152, 0, 0.6);
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 8px;
  font-size: 0.7rem;
  color: #f57c00;
  font-weight: 600;
  z-index: 1;
  pointer-events: none;
}

.time-off-bar i {
  font-size: 0.65rem;
  opacity: 0.9;
}

.time-off-bar span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Shift type and payer colors are now defined in global shift-colors.css */

/* Mobile: the 17-column time grid can't fit on a phone — let it scroll
   sideways inside its own container, keeping the day column pinned. */
@media (max-width: 768px) {
  .calendar-week-view {
    min-width: 740px;
  }

  /* Floor row heights so empty days still give a comfortable touch target */
  .calendar-week-view .calendar-row {
    min-height: 52px;
  }

  .calendar-week-view .date-label {
    position: sticky;
    left: 0;
    z-index: 6;
    width: 64px;
    font-size: 0.8rem;
    background: rgba(37, 55, 65, 0.97);
    box-shadow: 2px 0 4px rgba(0, 0, 0, 0.2);
  }

  .calendar-week-view .time-slot-header {
    font-size: 0.7rem;
  }
}
</style>
